// src/pages/friends-page/components/friends-card-info-modal/FriendsCardInfoModal.js
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  CloseButton,
  ModalBody,
  InfoRow,
  InfoLabel,
  InfoValue,
  SectionRow,
  VariationSection,
  VariationsScrollContainer,
  VariationItem,
  UncollectedVariationItem,
  VariationName,
  VariationRadio,
  EbayButton,
  ContentRow,
  RightContent,
} from './FriendsCardInfoModal.styles';

export default function FriendsCardInfoModal({
  isOpen,
  onClose,
  card,
  series,
  buildEbaySearchUrl,
  parallels = [],
  dispatchCardToQueue,
  handleRemoveCard,
  showcaseView = false
}) {
  const [copiedItem, setCopiedItem] = useState(null); // 'title' or 'description' or null

  // Initialize selected variation to first one when modal opens or card changes
  useEffect(() => {

    // Initialize qty values to 1 for each variation
    if (card?.collectedCards) {
      const initialQtyValues = {};
      card.collectedCards.forEach(variation => {
        initialQtyValues[variation.cardParallelName] = 1;
      });
      
    }
  }, [card?.collectedCards, isOpen]);

  // Clear copy confirmation after 3 seconds
  useEffect(() => {
    if (copiedItem) {
      const timer = setTimeout(() => {
        setCopiedItem(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [copiedItem]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (!isOpen) return;
    
    // Save current scroll position
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    
    // Store original overflow style
    const originalOverflow = document.body.style.overflow;
    
    // Simple approach: just prevent overflow without moving body
    // This avoids layout shifts that might cause re-render issues
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore original overflow
      document.body.style.overflow = originalOverflow;
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);


  if (!isOpen || !card) return null;

  const { name, cardNumber, collectedCards, quantity, team } = card;

  // Calculate uncollected variations
  const collectedVariationNames = new Set(
    (collectedCards || []).map(c => c.cardParallelName)
  );

  const uncollectedVariations = (parallels || []).filter(
    parallel => !collectedVariationNames.has(parallel.name)
  );

  const handleEbayClick = (e, parallel) => {
    e.stopPropagation();
    if (buildEbaySearchUrl && card && parallel) {
      try {
        const ebayUrl = buildEbaySearchUrl(card, parallel);
        if (ebayUrl) {
          window.open(ebayUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        console.error('Error building eBay URL:', error);
      }
    }
  };

  const modalContent = (
    <ModalOverlay onClick={onClose} className='panel-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()} className='panel-bg'>
        <ModalHeader>
          <SectionRow>
            <InfoRow>
              <InfoLabel>Card No.</InfoLabel>
              <InfoValue>{cardNumber}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{name && name.length > 1 ? 'Players:' : 'Player:'}</InfoLabel>
              <InfoValue>{name?.join(' • ') || 'N/A'}</InfoValue>
            </InfoRow>
            {(Array.isArray(team) && team.length > 0) || (team && typeof team === 'string' && team.trim()) ? (
              <InfoRow>
                <InfoLabel>{Array.isArray(team) && team.length > 1 ? 'Teams:' : 'Team:'}</InfoLabel>
                <InfoValue>{Array.isArray(team) ? team.join(' • ') : team}</InfoValue>
              </InfoRow>
            ) : null}
          </SectionRow>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentRow>
            {((collectedCards && collectedCards.length > 0) || uncollectedVariations.length > 0) && (
              <RightContent>
                {(collectedCards && collectedCards.length > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '48px' }}>
                    {quantity && (
                      <InfoRow>
                        <InfoLabel>Total Quantity:</InfoLabel>
                        <InfoValue>{quantity}</InfoValue>
                      </InfoRow>
                    )}
                    <InfoRow>
                      <InfoLabel>Variations Collected:</InfoLabel>
                      <InfoValue>{collectedCards.length}</InfoValue>
                    </InfoRow>
                  </div>
                )}
                
                <VariationsScrollContainer>
                  {(collectedCards && collectedCards.length > 0) && (
                    <VariationSection>
                      <InfoLabel>Collected Variations:</InfoLabel>
                      {collectedCards.map((parallel) => {
                        return (
                          <VariationItem key={parallel.cardParallelName}>
                            <VariationRadio>
                              <label htmlFor={`variation-${parallel.cardParallelName}`}>
                                <VariationName>
                                  {parallel.cardPrintNumber ? `${parallel.cardParallelName} (${parallel.cardPrintNumber}/${parallel.cardSupply})` : parallel.cardParallelName}
                                  {parallel.cardQty && ` - QTY: ${parallel.cardQty}`}
                                </VariationName>
                              </label>
                            </VariationRadio>
                            <EbayButton onClick={(e) => handleEbayClick(e, parallel)} title="Search this variation on eBay (sold listings)">Search eBay</EbayButton>
                          </VariationItem>
                        );
                      })}
                    </VariationSection>
                  )}

                  {uncollectedVariations.length > 0 && (
                    <VariationSection>
                      <InfoLabel>Uncollected Variations:</InfoLabel>
                      {uncollectedVariations.map((parallel) => {
                        const parallelForEbay = { cardParallelName: parallel.name, cardColor: parallel.color, cardPrintNumber: null, cardSupply: parallel.limitedTo || null };
                        return (
                          <UncollectedVariationItem key={parallel.name}>
                            <div style={{ flex: 1 }}>
                              <VariationName>{parallel.name}{parallel.limitedTo && ` /${parallel.limitedTo}`}</VariationName>
                            </div>
                            <EbayButton onClick={(e) => handleEbayClick(e, parallelForEbay)} title="Search this variation on eBay (sold listings)">Search eBay</EbayButton>
                          </UncollectedVariationItem>
                        );
                      })}
                    </VariationSection>
                  )}
                </VariationsScrollContainer>
              </RightContent>
            )}
          </ContentRow>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
